(() => {
  if (customElements.get('variant-media-controller')) return;

  class VariantMediaController extends HTMLElement {
    connectedCallback() {
      this.onReady ??= () => this.initialize();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.onReady, { once: true });
      } else {
        queueMicrotask(this.onReady);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.onReady);
    }

    initialize() {
      if (!this.isConnected || this.initialized) return;
      const source = this.querySelector('[data-variant-media-data]');
      if (!source) return;
      try {
        this.data = JSON.parse(source.textContent);
      } catch (error) {
        console.error('Unable to read native variant gallery data.', error);
        return;
      }
      this.root = this.closest('.product');
      if (!this.root) return;
      this.owner = this.closest('quick-add-modal') || this.closest('.shopify-section') || this.closest('section') || this.root;
      this.initialized = true;
      this.mediaIds = this.data.mediaIds.map(String);
      this.selectedOptions = this.data.initialOptions || [];
      this.selectedVariantId = this.data.initialVariantId;
      const pickerSource = this.root.querySelector('[data-variant-picker-data]');
      const initialVariant = pickerSource ? JSON.parse(pickerSource.textContent).variants.find(variant => variant.id === this.selectedVariantId) : null;
      this.selectedMediaIds = this.resolveMedia(initialVariant, this.selectedOptions);
      this.syncAllGalleries(false);
      customElements.whenDefined('media-gallery').then(() => {
        if (this.isConnected) this.syncAllGalleries(false);
      });
    }

    resolveMedia(variant, options = []) {
      const value = options[this.data.optionPosition - 1];
      const group = this.data.groups.find(item => item.value === value);
      const groupIds = (group?.mediaIds || []).map(String).filter(id => this.mediaIds.includes(id));
      if (groupIds.length) return [...new Set(groupIds)];
      const featuredId = String(variant?.featured_media?.id || '');
      if (this.mediaIds.includes(featuredId)) return [featuredId];
      return this.mediaIds.slice();
    }

    applySelection(variant, options) {
      this.initialize();
      if (!this.initialized) return false;
      const ids = this.resolveMedia(variant, options);
      const reset = ids.join(',') !== this.selectedMediaIds.join(',');
      this.selectedOptions = options.slice();
      this.selectedVariantId = variant?.id || null;
      this.selectedMediaIds = ids;
      this.syncAllGalleries(reset);
      if (!variant) {
        this.root.querySelectorAll('[id^="Sku-"], [id^="Inventory-"], [id^="stock-"]').forEach(element => element.classList.add("visibility-hidden"));
      }
      return true;
    }

    syncAllGalleries(reset) {
      this.root.querySelectorAll('media-gallery[data-native-variant-gallery]').forEach(gallery => this.syncGallery(gallery, reset));
      this.owner.querySelectorAll('product-modal[data-native-variant-gallery]').forEach(modal => {
        if (modal.dataset.sectionId === this.dataset.sectionId) this.syncModal(modal);
      });
    }

    syncGallery(gallery, reset = false) {
      if (!this.initialized || !this.root.contains(gallery)) return;
      if (typeof gallery.applyMediaGroup === 'function') {
        gallery.applyMediaGroup(this.selectedMediaIds, reset);
      } else {
        this.filterItems(gallery);
      }
    }

    filterItems(container) {
      container.querySelectorAll('[data-variant-media-id]').forEach(item => {
        const visible = this.selectedMediaIds.includes(item.dataset.variantMediaId);
        item.hidden = !visible;
        item.toggleAttribute('data-variant-media-hidden', !visible);
        if (visible) item.removeAttribute('aria-hidden');
        else item.setAttribute('aria-hidden', 'true');
      });
    }

    syncModal(modal) {
      this.filterItems(modal);
      const content = modal.querySelector('.product-media-modal__content');
      if (!content) return;
      content.querySelectorAll('[data-variant-media-hidden]').forEach(item => item.classList.remove('active'));
    }

    getState(variantId = this.selectedVariantId) {
      this.initialize();
      return this.data?.states[String(variantId)];
    }

    renderProductInfo(variant) {
      const state = this.getState(variant.id);
      if (!state) return false;
      const find = prefix => this.root.querySelector(`[id="${prefix}-${this.dataset.sectionId}"]`);
      const update = (prefix, markup, visible = true) => {
        const element = find(prefix);
        if (!element) return;
        element.innerHTML = markup;
        element.classList.toggle('visibility-hidden', !visible);
      };
      update('price', state.priceHtml);
      update('Sku', state.skuHtml, Boolean(variant.sku));
      update('Inventory', state.inventoryHtml, Boolean(state.inventoryHtml.trim()));
      update('stock', state.stockHtml);
      find('stock')?.classList.toggle('hidden', !state.stockHtml.trim());

      const html = new DOMParser().parseFromString(state.quantityHtml, 'text/html');
      const quantityInput = this.root.querySelector('.quantity__input');
      const nextInput = html.querySelector('.quantity__input');
      const cartItems = window.cartBase?.cartData?.items;
      if (nextInput && Array.isArray(cartItems)) {
        const count = cartItems.filter(item => String(item.variant_id || item.id) === String(variant.id))
          .reduce((total, item) => total + item.quantity, 0);
        nextInput.dataset.cartQuantity = String(count);
      }
      if (quantityInput && nextInput) {
        ['data-cart-quantity', 'min', 'max', 'step'].forEach(attribute => {
          if (nextInput.hasAttribute(attribute)) quantityInput.setAttribute(attribute, nextInput.getAttribute(attribute));
          else quantityInput.removeAttribute(attribute);
        });
        quantityInput.dataset.min = nextInput.min || '1';
        if (nextInput.hasAttribute('max')) quantityInput.dataset.max = nextInput.max;
        else delete quantityInput.dataset.max;
        quantityInput.value = nextInput.min || '1';
      }
      ['Quantity-Rules', 'Volume', 'Price-Per-Item'].forEach(prefix => {
        const element = find(prefix);
        const replacement = html.getElementById(`${prefix}-${this.dataset.sectionId}`);
        if (element && replacement) {
          element.innerHTML = replacement.innerHTML;
          if (prefix === 'Price-Per-Item') element.dataset.variantId = String(variant.id);
        }
      });
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId: this.dataset.sectionId, variant, html } });
      }
      return true;
    }
  }

  customElements.define('variant-media-controller', VariantMediaController);
})();
